import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'components/Breadcrumbs';
import ChemicalCard from 'components/ChemicalCard';
import {
  Sidebar,
  SummarySection,
  StorageSection,
  FlammabilitySection,
  ReactivitySection,
  HandlingSection,
  PPESection,
  FirstAidSection,
  FireSafetySection,
  ToxicitySection,
  TransportationSection,
  EmergencySection
} from 'components/MSDSData';
import { getSDS } from 'utils/methods/safetynow';
import { getAllGHSImages } from 'utils/helpers';
import { useLocationStore } from 'context/LocationContext';

const SafetyNowDatasheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedLocationProfile } = useLocationStore();

  const [chemicalDetails, setChemicalDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('basic-info');
  const [activePpeTab, setActivePpeTab] = useState(null);
  const [summaryData, setSummaryData] = useState({
    hazards: [],
    precautions: []
  });
  const [refreshKey, setRefreshKey] = useState(0);
  
  const sidebarSections = {
    'basic-info': { label: 'Summary', icon: 'fas fa-info-circle' },
    'storage': { label: 'Storage', icon: 'fas fa-warehouse' },
    'handling': { label: 'Handling', icon: 'fas fa-hand-holding' },
    'flammability': { label: 'Flammability', icon: 'fas fa-fire' },
    'toxicity': { label: 'Toxicity', icon: 'fas fa-skull-crossbones' },
    'reactivity': { label: 'Reactivity & stability', icon: 'fas fa-flask' },
    'ppe': { label: 'Personal Protective Equipment', icon: 'fas fa-shield-alt' },
    'first-aid': { label: 'First Aid', icon: 'fas fa-plus-circle' },
    'fire-safety': { label: 'Fire Safety', icon: 'fas fa-fire-extinguisher' },
    'transportation': { label: 'Transportation Guidelines', icon: 'fas fa-truck' },
    'emergency': { label: 'Emergency Guidelines', icon: 'fas fa-exclamation-triangle' }
  };

  const fetchChemicalDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSDS(id);
      console.log('SDS Response:', response.data);
      
      // Check if extracted_data exists and has the processed data
      if (!response.data.extracted_data) {
        throw new Error('No processed data available for this SDS');
      }

      const msdsData = response.data.extracted_data;
      
      // Transform the MSDS data to match the chemical profile structure
      const transformedData = transformMSDSToChemicalProfile(msdsData);
      
      setChemicalDetails(transformedData);
      setSummaryDataFromProfile(transformedData);

      if (transformedData.ppe) {
        setActivePpeTab(Object.keys(transformedData.ppe)[0]);
      }

    } catch (err) {
      console.error('Error fetching SDS:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load SDS data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchChemicalDetails();
    } else {
      navigate('/tools/safety-now');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Add effect to re-render when location changes
  useEffect(() => {
    // Force re-render by updating refresh key when location changes
    if (selectedLocationProfile) {
      setRefreshKey(prev => prev + 1);
    }
  }, [selectedLocationProfile]);

  // Transform MSDS data to chemical profile structure
  const transformMSDSToChemicalProfile = (msdsData) => {
    return {
      // Basic Information
      name: msdsData.identity?.name || 'Unknown Chemical',
      cas_number: msdsData.composition?.[0]?.cas || '',
      manufacturer: msdsData.identity?.provider?.organization || '',
      manufacturer_location: msdsData.identity?.provider?.location || '',
      manufacturer_phone: msdsData.identity?.provider?.contact?.phone || '',
      revision_date: msdsData.otherInformation?.updated || '',
      emergency_contact: msdsData.identity?.provider?.contact?.emergency || '',
      aliases: msdsData.identity?.aliases || [],
      product_number: msdsData.identity?.aliases?.[0] || '',
      
      // Composition - preserve all chemicals in the mixture
      composition: msdsData.composition || [],
      
      // Hazards - extract H-codes from statements if present, otherwise create generic codes
      hazards: msdsData.hazards?.statements ? msdsData.hazards.statements.map((statement, idx) => {
        // Try to extract H-code from the statement (e.g., "H300: Fatal if swallowed")
        const hCodeMatch = statement.match(/^(H\d{3}[\w+]*)/);
        const code = hCodeMatch ? hCodeMatch[1] : null;
        const cleanStatement = code ? statement.replace(/^H\d{3}[\w+]*:?\s*/, '') : statement;
        
        return {
          code: code || `UNKNOWN_${idx}`, // Use extracted code or mark as unknown
          statement: cleanStatement,
          image: null
        };
      }) : [],
      signal_word: msdsData.hazards?.signal || '',
      
      // Precautions
      precautions: [
        ...(msdsData.hazards?.precautions?.prevention || []).map(p => ({ statement: p, type: 'prevention' })),
        ...(msdsData.hazards?.precautions?.response || []).map(p => ({ statement: p, type: 'response' })),
        ...(msdsData.hazards?.precautions?.storage || []).map(p => ({ statement: p, type: 'storage' })),
        ...(msdsData.hazards?.precautions?.disposal || []).map(p => ({ statement: p, type: 'disposal' }))
      ],
      
      // Storage
      storage: {
        conditions: msdsData.handlingAndStorage?.storage || [],
        incompatibilities: msdsData.stabilityAndReactivity?.incompatible || msdsData.handlingAndStorage?.incompatibles || [],
        melting_point: msdsData.properties?.melting || '',
        boiling_point: msdsData.properties?.boiling || '',
        flash_point: msdsData.properties?.flash || '',
        vapor_pressure: msdsData.properties?.vaporPressure || '',
        vapor_density: msdsData.properties?.vaporDensity || '',
        density: msdsData.properties?.density || '',
        solubility: msdsData.properties?.solubility || '',
        decomposition_temperature: msdsData.properties?.decomposition || ''
      },
      
      // Handling
      handling: {
        procedures: msdsData.handlingAndStorage?.procedures || [],
        engineering_controls: msdsData.exposureControls?.engineering || '',
        spill_procedures: msdsData.accidentalMeasures?.cleanup || [],
        spill_precautions: msdsData.accidentalMeasures?.precautions || [],
        spill_containment: msdsData.accidentalMeasures?.containment || []
      },
      
      // Flammability
      flammability: {
        flash_point: msdsData.properties?.flash || '',
        autoignition_temperature: msdsData.properties?.autoignition || '',
        flammable_limits_lower: msdsData.properties?.limits?.lower || '',
        flammable_limits_upper: msdsData.properties?.limits?.upper || '',
        extinguishing_media: msdsData.fireMeasures?.extinguishers || [],
        unsuitable_extinguishing_media: [],
        fire_hazards: msdsData.fireMeasures?.risks || []
      },
      
      // Reactivity
      reactivity_and_stability: {
        stability: msdsData.stabilityAndReactivity?.stability?.[0] || '',
        reactivity_description: msdsData.stabilityAndReactivity?.reactivity || [],
        conditions_to_avoid: msdsData.stabilityAndReactivity?.avoid || [],
        incompatible_materials: msdsData.stabilityAndReactivity?.incompatible || [],
        hazardous_decomposition: msdsData.stabilityAndReactivity?.decompositionProducts || [],
        hazardous_reactions: msdsData.stabilityAndReactivity?.reactions || []
      },
      
      // PPE
      ppe: {
        eyes: msdsData.exposureControls?.ppe?.eyes || '',
        skin: msdsData.exposureControls?.ppe?.skin || '',
        respiratory: msdsData.exposureControls?.ppe?.respirator || '',
        engineering_controls: msdsData.exposureControls?.engineering || ''
      },
      
      // First Aid
      first_aid: {
        inhalation: msdsData.firstAid?.inhalation || [],
        skin: msdsData.firstAid?.skin || [],
        eyes: msdsData.firstAid?.eyes || [],
        ingestion: msdsData.firstAid?.ingestion || []
      },
      
      // Fire Safety
      fire_fighting: {
        extinguishing_media: msdsData.fireMeasures?.extinguishers || [],
        specific_hazards: msdsData.fireMeasures?.risks || [],
        protective_equipment: msdsData.fireMeasures?.protection || ''
      },
      
      // Toxicity
      toxicity: {
        acute: msdsData.toxicology?.acute ? [msdsData.toxicology.acute] : [],
        chronic: msdsData.toxicology?.chronic ? [msdsData.toxicology.chronic] : [],
        routes_of_exposure: msdsData.toxicology?.routes || [],
        ld50_oral: msdsData.toxicology?.ld50Oral || '',
        ld50_dermal: msdsData.toxicology?.ld50Dermal || '',
        lc50_inhalation: msdsData.toxicology?.lc50Inhalation || '',
        carcinogenicity: msdsData.toxicology?.carcinogenicity || ''
      },
      
      // Transportation (using transportation_guidelines to match component expectations)
      transportation_guidelines: {
        un_number: msdsData.transportation?.un || '',
        proper_shipping_name: msdsData.transportation?.name || '',
        transport_class: msdsData.transportation?.class || '',
        packing_group: msdsData.transportation?.group || '',
        emergency_guide: msdsData.transportation?.guide || ''
      },
      
      // Emergency
      emergency: {
        spill_procedures: msdsData.accidentalMeasures?.cleanup || [],
        spill_precautions: msdsData.accidentalMeasures?.precautions || [],
        spill_containment: msdsData.accidentalMeasures?.containment || [],
        waste_disposal: msdsData.disposal?.method ? [msdsData.disposal.method] : [],
        disposal_code: msdsData.disposal?.code || ''
      },
      
      // Environmental
      environmental: {
        aquatic_toxicity: msdsData.ecology?.aquatic || '',
        biodegradability: msdsData.ecology?.biodegradability || ''
      },
      
      // Regulatory
      regulatory: {
        tsca: msdsData.regulatory?.tsca || '',
        sara313: msdsData.regulatory?.sara313 || '',
        reach: msdsData.regulatory?.reach || ''
      },
      
      // Physical Properties
      physical_properties: {
        appearance: msdsData.properties?.appearance || '',
        odor: msdsData.properties?.odor || '',
        odor_threshold: msdsData.properties?.odorThreshold || '',
        pH: msdsData.properties?.pH || '',
        melting_point: msdsData.properties?.melting || '',
        boiling_point: msdsData.properties?.boiling || '',
        flash_point: msdsData.properties?.flash || '',
        evaporation_rate: msdsData.properties?.evaporation || '',
        vapor_pressure: msdsData.properties?.vaporPressure || '',
        vapor_density: msdsData.properties?.vaporDensity || '',
        density: msdsData.properties?.density || '',
        solubility: msdsData.properties?.solubility || '',
        partition_coefficient: msdsData.properties?.logKow || '',
        autoignition_temperature: msdsData.properties?.autoignition || '',
        decomposition_temperature: msdsData.properties?.decomposition || '',
        viscosity: msdsData.properties?.viscosity || ''
      },
      
      // Exposure Limits
      exposure_limits: {
        pel: msdsData.exposureControls?.limits?.pel || '',
        tlv: msdsData.exposureControls?.limits?.tlv || ''
      }
    };
  };

  const setSummaryDataFromProfile = (data) => {
    const newSummaryData = {
      precautions: [],
      hazards: [],
      signal_word: data.signal_word || ''
    };

    // Handle precautions - check if it exists and has the right structure
    if (data.precautions && Array.isArray(data.precautions)) {
      newSummaryData.precautions = data.precautions.map(item => {
        // If item has statement property, use it; otherwise use the item directly
        return item;
      });
    }

    // Handle hazards - just pass them through without image mapping
    if (data.hazards && Array.isArray(data.hazards)) {
      const hazards = data.hazards.map(hazard => ({
        code: hazard.code,
        statement: hazard.statement,
      }));
      
      newSummaryData.hazards = getAllGHSImages(hazards)
    }

    setSummaryData(newSummaryData);
  };

  const currentTemperature = useMemo(() => {
    const temp = parseFloat(selectedLocationProfile?.temperature || 0);
    return temp;
  }, [selectedLocationProfile]);

  const isAboveTemperaturePoint = useMemo(() => {
    return (tempPoint) => {
      if (!tempPoint) return false;

      if (typeof tempPoint !== 'string') return currentTemperature > tempPoint;

      // Try to extract Celsius value first
      const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
      if (celsiusMatch) {
        const temp = parseFloat(celsiusMatch[1]);
        return !isNaN(temp) && currentTemperature > temp;
      }

      // Fallback to Fahrenheit if Celsius not found
      const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
      if (fahrenheitMatch) {
        const tempF = parseFloat(fahrenheitMatch[1]);
        // Convert Fahrenheit to Celsius for comparison
        const tempC = (tempF - 32) * (5 / 9);
        return !isNaN(tempC) && currentTemperature > tempC;
      }
      return false;
    };
  }, [currentTemperature]);

  // Helper function to extract temperature value in Celsius
  const getTemperatureInCelsius = (tempPoint) => {
    if (!tempPoint) return null;
    
    if (typeof tempPoint !== 'string') return tempPoint;

    // Try to extract Celsius value first
    const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
    if (celsiusMatch) {
      const temp = parseFloat(celsiusMatch[1]);
      return !isNaN(temp) ? temp : null;
    }

    // Fallback to Fahrenheit if Celsius not found
    const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
    if (fahrenheitMatch) {
      const tempF = parseFloat(fahrenheitMatch[1]);
      // Convert Fahrenheit to Celsius
      const tempC = (tempF - 32) * (5 / 9);
      return !isNaN(tempC) ? tempC : null;
    }
    return null;
  };

  // Check if substance is solid (melting point > 100°C)
  const isSolid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp > 100;
  }, [chemicalDetails]);

  const isLiquid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp < 0;
  }, [chemicalDetails]);

  // Check if substance is gas (boiling point < 0°C)
  const isGas = useMemo(() => {
    const boilingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.boiling_point);
    return boilingPointTemp !== null && boilingPointTemp < 0;
  }, [chemicalDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-8 border-t-8 border-gray-200 border-t-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !chemicalDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error || 'Unable to load SDS data'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50" key={refreshKey}>
      <div className="w-full px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 py-8 bg-gradient-to-br from-gray-50 to-blue-50">

        <div className="col-span-5">
          <Breadcrumbs />
        </div>

        {/* Sidebar */}
        <div className="col-span-1">
          <Sidebar 
            sidebarSections={sidebarSections}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        </div>

        {/* Main content */}
        <div className="col-span-5 md:col-span-2 lg:col-span-4 space-y-8">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Chemical Card */}
          {chemicalDetails && (
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
              <ChemicalCard chemicalDetails={chemicalDetails} />
            </div>
          )}

          {/* Content Sections */}
          {chemicalDetails && (
            <div className="space-y-8">

              {/* Basic Information / Summary Section */}
              {activeSection === 'basic-info' && (
                <SummarySection 
                  summaryData={summaryData}
                />
              )}

              {/* Storage Information */}
              {activeSection === 'storage' && (
                <StorageSection 
                  chemicalDetails={chemicalDetails}
                  isGas={isGas}
                  isSolid={isSolid}
                  isLiquid={isLiquid}
                  isAboveTemperaturePoint={isAboveTemperaturePoint}
                />
              )}

              {/* Flammability Information */}
              {activeSection === 'flammability' && (
                <FlammabilitySection 
                  chemicalDetails={chemicalDetails}
                  isGas={isGas}
                  isAboveTemperaturePoint={isAboveTemperaturePoint}
                />
              )}

              {/* Reactivity Information */}
              {activeSection === 'reactivity' && (
                <ReactivitySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Handling */}
              {activeSection === 'handling' && (
                <HandlingSection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* PPE */}
              {activeSection === 'ppe' && (
                <PPESection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Fire Safety */}
              {activeSection === 'fire-safety' && (
                <FireSafetySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* First Aid */}
              {activeSection === 'first-aid' && (
                <FirstAidSection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Toxicity */}
              {activeSection === 'toxicity' && (
                <ToxicitySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Transportation Guidelines */}
              {activeSection === 'transportation' && (
                <TransportationSection chemicalDetails={chemicalDetails} />
              )}

              {/* Emergency Guidelines */}
              {activeSection === 'emergency' && (
                <EmergencySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyNowDatasheet;
